/**
 * Static Dependency Rules
 * Automatically inject dependencies based on entity conditions
 */

import { StaticDependencyRule } from '@/interfaces/stateManagement/DependencyInterfaces';
import { EntityType } from '@/interfaces/entities/EntityType';
import { EdgeType } from '@/interfaces/entities/EdgeType';
import { EndPointInterface } from '@/interfaces/entities/entity_blueprints/EndPointInterface';
import { MiddlewareInterface } from '@/interfaces/entities/entity_blueprints/MiddlewareInterface';
import { ModelPropertyType } from '@/interfaces/entities/entity_blueprints/ModelPropertyInterface';
import { ModelInterface } from '@/interfaces/entities/entity_blueprints/ModelInterface';
import { isBaseEntity } from '@/interfaces/entities/BaseEntityInterface';

/**
 * Built-in Static Dependency Rules
 * These rules automatically inject common dependencies
 */
export const BUILT_IN_STATIC_DEPENDENCY_RULES: StaticDependencyRule[] = [
    // ========================================
    // Rule 1: File Upload Middleware Injection
    // ========================================
    {
        id: "endpoint-file-model-to-fileupload-middleware",
        name: "Endpoints with File Models → fileUploadMiddleware",
        condition: (entity, context) => {
            if (!isBaseEntity(entity) || entity.entityType !== EntityType.ENDPOINT) return false;
            const endpoint = entity as unknown as EndPointInterface;

            // Check if endpoint uses models with File properties
            if (!endpoint.modelsRequired || endpoint.modelsRequired.length === 0) {
                return false;
            }

            // Get models as ModelInterface[] (not BackendModel[])
            const models = context.entityManager.getModels() as unknown as ModelInterface[];
            const hasFileModel = endpoint.modelsRequired.some(modelId => {
                const model = models.find(m => m.id === modelId || m.name === modelId);
                if (!model) return false;

                // Check if model has File properties
                return model.properties?.some(p => p.type === ModelPropertyType.File) || false;
            });

            if (!hasFileModel) return false;

            // Check if fileUpload middleware already attached
            const fileUploadMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "fileUploadMiddleware" || m.id === "fileUploadMiddleware");

            if (!fileUploadMiddleware) {
                console.warn('[Static Dependency] fileUploadMiddleware not found in system');
                return false;
            }

            // Check if already attached
            if (endpoint.middlewareIds?.includes(fileUploadMiddleware.id)) {
                return false;
            }

            return true;
        },
        injectDependencies: (entity, context) => {
            const endpoint = entity as unknown as EndPointInterface;
            const fileUploadMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "fileUploadMiddleware" || m.id === "fileUploadMiddleware");

            if (!fileUploadMiddleware) {
                return [];
            }

            console.log(`[Static Dependency] Auto-injecting fileUploadMiddleware to endpoint ${endpoint.id}`);

            return [{
                id: `${endpoint.id}-${fileUploadMiddleware.id}-static-fileupload`,
                sourceId: endpoint.id,
                targetId: fileUploadMiddleware.id,
                edgeType: EdgeType.ENDPOINT_TO_MIDDLEWARE,
                isStatic: true,
                isRequired: false,
                metadata: {
                    autoInjected: true,
                    reason: "File model detected",
                    ruleName: "endpoint-file-model-to-fileupload-middleware"
                },
            }];
        },
        priority: 10,
        required: false,
    },

    // ========================================
    // Rule 2: Auth Middleware Injection
    // ========================================
    {
        id: "auth-required-endpoint-to-auth-middleware",
        name: "Auth-Required Endpoints → authMiddleware",
        condition: (entity, context) => {
            if (!isBaseEntity(entity) || entity.entityType !== EntityType.ENDPOINT) return false;
            const endpoint = entity as unknown as EndPointInterface;

            // Check if endpoint requires auth
            if (!endpoint.requiresAuth) {
                return false;
            }

            // Don't inject for auth endpoints themselves
            const isAuthEndpoint = endpoint.path?.includes('/auth') ||
                endpoint.path?.includes('/login') ||
                endpoint.path?.includes('/register') ||
                endpoint.path?.includes('/signup');

            if (isAuthEndpoint) {
                return false;
            }

            // Check if auth middleware exists
            const authMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "authMiddleware" || m.middlewareName === "AuthenticationMiddleware" || m.id === "authMiddleware" || m.id === "auth-middleware");

            if (!authMiddleware) {
                console.warn('[Static Dependency] authMiddleware not found in system');
                return false;
            }

            // Check if already attached
            if (endpoint.middlewareIds?.includes(authMiddleware.id)) {
                return false;
            }

            return true;
        },
        injectDependencies: (entity, context) => {
            const endpoint = entity as unknown as EndPointInterface;
            const authMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "authMiddleware" || m.middlewareName === "AuthenticationMiddleware" || m.id === "authMiddleware" || m.id === "auth-middleware");

            if (!authMiddleware) {
                return [];
            }

            console.log(`[Static Dependency] Auto-injecting authMiddleware to endpoint ${endpoint.id}`);

            return [{
                id: `${endpoint.id}-${authMiddleware.id}-static-auth`,
                sourceId: endpoint.id,
                targetId: authMiddleware.id,
                edgeType: EdgeType.ENDPOINT_TO_MIDDLEWARE,
                isStatic: true,
                isRequired: false,
                metadata: {
                    autoInjected: true,
                    reason: "Authentication required",
                    ruleName: "auth-required-endpoint-to-auth-middleware"
                },
            }];
        },
        priority: 10,
        required: false,
    },

    // ========================================
    // Rule 3: Auth Service Injection to Auth Middleware
    // ========================================
    {
        id: "auth-middleware-to-auth-service",
        name: "authMiddleware → authService",
        condition: (entity, context) => {
            if (!isBaseEntity(entity) || entity.entityType !== EntityType.MIDDLEWARE) return false;
            const middleware = entity as unknown as MiddlewareInterface;

            // Check if this is the auth middleware
            const isAuthMiddleware = middleware.middlewareName === "authMiddleware" ||
                middleware.middlewareName === "AuthenticationMiddleware" ||
                middleware.id === "authMiddleware" ||
                middleware.id === "auth-middleware";

            if (!isAuthMiddleware) {
                return false;
            }

            // Check if auth service exists
            const authService = context.entityManager
                .getServices()
                .find(s => s.serviceName === "authService" || s.id === "authService");

            if (!authService) {
                console.warn('[Static Dependency] authService not found in system');
                return false;
            }

            // Check if already attached (use servicesRequired for consistency)
            const servicesNeeded = (middleware as any).servicesNeeded || middleware.servicesRequired || [];
            if (servicesNeeded.includes(authService.id)) {
                return false;
            }

            return true;
        },
        injectDependencies: (entity, context) => {
            const middleware = entity as unknown as MiddlewareInterface;
            const authService = context.entityManager
                .getServices()
                .find(s => s.serviceName === "authService" || s.id === "authService");

            if (!authService) {
                return [];
            }

            console.log(`[Static Dependency] Auto-injecting authService to authMiddleware`);

            return [{
                id: `${middleware.id}-${authService.id}-static-authservice`,
                sourceId: middleware.id,
                targetId: authService.id,
                edgeType: EdgeType.MIDDLEWARE_TO_SERVICE,
                isStatic: true,
                isRequired: true,
                metadata: {
                    autoInjected: true,
                    reason: "Auth middleware requires auth service",
                    ruleName: "auth-middleware-to-auth-service"
                },
            }];
        },
        priority: 10,
        required: true,
    },

    // ========================================
    // Rule 4: CORS Middleware Injection (All Public Endpoints)
    // ========================================
    {
        id: "public-endpoint-to-cors-middleware",
        name: "Public Endpoints → corsMiddleware",
        condition: (entity, context) => {
            if (!isBaseEntity(entity) || entity.entityType !== EntityType.ENDPOINT) return false;
            const endpoint = entity as unknown as EndPointInterface;

            // Check if endpoint is public (no auth required)
            if (endpoint.requiresAuth) {
                return false;
            }

            // Check if CORS middleware exists
            const corsMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "corsMiddleware" || m.id === "corsMiddleware");

            if (!corsMiddleware) {
                // CORS middleware is optional
                return false;
            }

            // Check if already attached
            if (endpoint.middlewareIds?.includes(corsMiddleware.id)) {
                return false;
            }

            return true;
        },
        injectDependencies: (entity, context) => {
            const endpoint = entity as unknown as EndPointInterface;
            const corsMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "corsMiddleware" || m.id === "corsMiddleware");

            if (!corsMiddleware) {
                return [];
            }

            console.log(`[Static Dependency] Auto-injecting corsMiddleware to endpoint ${endpoint.id}`);

            return [{
                id: `${endpoint.id}-${corsMiddleware.id}-static-cors`,
                sourceId: endpoint.id,
                targetId: corsMiddleware.id,
                edgeType: EdgeType.ENDPOINT_TO_MIDDLEWARE,
                isStatic: true,
                isRequired: false,
                metadata: {
                    autoInjected: true,
                    reason: "Public endpoint requires CORS",
                    ruleName: "public-endpoint-to-cors-middleware"
                },
            }];
        },
        priority: 5, // Lower priority than auth
        required: false,
    },

    // ========================================
    // Rule 5: RBAC Access Middleware Injection
    // ========================================
    {
        id: "rbac-enabled-endpoint-to-rbac-access-middleware",
        name: "RBAC-Enabled Auth Endpoints → rbacAccessMiddleware",
        condition: (entity, context) => {
            if (!isBaseEntity(entity) || entity.entityType !== EntityType.ENDPOINT) return false;
            const endpoint = entity as unknown as EndPointInterface;

            // Check if RBAC is enabled in metadata
            const metaData = context.entityManager.getMetaData?.();
            if (!metaData?.rbac?.enabled) {
                return false;
            }

            // Only inject for endpoints that require auth
            if (!endpoint.requiresAuth) {
                return false;
            }

            // Don't inject for auth endpoints themselves
            const isAuthEndpoint = endpoint.path?.includes('/auth') ||
                endpoint.path?.includes('/login') ||
                endpoint.path?.includes('/register') ||
                endpoint.path?.includes('/signup');

            if (isAuthEndpoint) {
                return false;
            }

            // Check if RBAC access middleware exists
            const rbacAccessMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "rbacAccessMiddleware" || m.id === "rbacAccessMiddleware" || m.id === "rbac-access-middleware");

            if (!rbacAccessMiddleware) {
                console.warn('[Static Dependency] rbacAccessMiddleware not found in system');
                return false;
            }

            // Check if already attached
            if (endpoint.middlewareIds?.includes(rbacAccessMiddleware.id)) {
                return false;
            }

            return true;
        },
        injectDependencies: (entity, context) => {
            const endpoint = entity as unknown as EndPointInterface;
            const rbacAccessMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "rbacAccessMiddleware" || m.id === "rbacAccessMiddleware" || m.id === "rbac-access-middleware");

            if (!rbacAccessMiddleware) {
                return [];
            }

            console.log(`[Static Dependency] Auto-injecting rbacAccessMiddleware to endpoint ${endpoint.id}`);

            return [{
                id: `${endpoint.id}-${rbacAccessMiddleware.id}-static-rbac-access`,
                sourceId: endpoint.id,
                targetId: rbacAccessMiddleware.id,
                edgeType: EdgeType.ENDPOINT_TO_MIDDLEWARE,
                isStatic: true,
                isRequired: false,
                metadata: {
                    autoInjected: true,
                    reason: "RBAC enabled and authentication required",
                    ruleName: "rbac-enabled-endpoint-to-rbac-access-middleware"
                },
            }];
        },
        priority: 9, // After auth middleware (priority 10)
        required: false,
    },

    // ========================================
    // Rule 6: RBAC Key Filter Middleware Injection
    // ========================================
    {
        id: "rbac-enabled-endpoint-to-rbac-key-filter-middleware",
        name: "RBAC-Enabled Auth Endpoints → rbacKeyFilterMiddleware",
        condition: (entity, context) => {
            if (!isBaseEntity(entity) || entity.entityType !== EntityType.ENDPOINT) return false;
            const endpoint = entity as unknown as EndPointInterface;

            // Check if RBAC is enabled in metadata
            const metaData = context.entityManager.getMetaData?.();
            if (!metaData?.rbac?.enabled) {
                return false;
            }

            // Only inject for endpoints that require auth
            if (!endpoint.requiresAuth) {
                return false;
            }

            // Don't inject for auth endpoints themselves
            const isAuthEndpoint = endpoint.path?.includes('/auth') ||
                endpoint.path?.includes('/login') ||
                endpoint.path?.includes('/register') ||
                endpoint.path?.includes('/signup');

            if (isAuthEndpoint) {
                return false;
            }

            // Check if RBAC key filter middleware exists
            const rbacKeyFilterMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "rbacKeyFilterMiddleware" || m.id === "rbacKeyFilterMiddleware" || m.id === "rbac-key-filter-middleware");

            if (!rbacKeyFilterMiddleware) {
                console.warn('[Static Dependency] rbacKeyFilterMiddleware not found in system');
                return false;
            }

            // Check if already attached
            if (endpoint.middlewareIds?.includes(rbacKeyFilterMiddleware.id)) {
                return false;
            }

            return true;
        },
        injectDependencies: (entity, context) => {
            const endpoint = entity as unknown as EndPointInterface;
            const rbacKeyFilterMiddleware = context.entityManager
                .getMiddlewares()
                .find(m => m.middlewareName === "rbacKeyFilterMiddleware" || m.id === "rbacKeyFilterMiddleware" || m.id === "rbac-key-filter-middleware");

            if (!rbacKeyFilterMiddleware) {
                return [];
            }

            console.log(`[Static Dependency] Auto-injecting rbacKeyFilterMiddleware to endpoint ${endpoint.id}`);

            return [{
                id: `${endpoint.id}-${rbacKeyFilterMiddleware.id}-static-rbac-key-filter`,
                sourceId: endpoint.id,
                targetId: rbacKeyFilterMiddleware.id,
                edgeType: EdgeType.ENDPOINT_TO_MIDDLEWARE,
                isStatic: true,
                isRequired: false,
                metadata: {
                    autoInjected: true,
                    reason: "RBAC enabled and authentication required",
                    ruleName: "rbac-enabled-endpoint-to-rbac-key-filter-middleware"
                },
            }];
        },
        priority: 8, // After RBAC access middleware (priority 9)
        required: false,
    },
];

/**
 * Helper function to add a new static dependency rule at runtime
 */
export function createStaticDependencyRule(
    id: string,
    name: string,
    condition: StaticDependencyRule['condition'],
    injectDependencies: StaticDependencyRule['injectDependencies'],
    options: {
        priority?: number;
        required?: boolean;
    } = {}
): StaticDependencyRule {
    return {
        id,
        name,
        condition,
        injectDependencies,
        priority: options.priority ?? 5,
        required: options.required ?? false,
    };
}
